import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import {
  ArrowLeft,
  Heart,
  Loader2,
  ExternalLink,
  Calendar,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdDetail = () => {
  const { adId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    fetchAd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const fetchAd = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ads/${adId}`);
      setAd(response.data);
    } catch (error) {
      toast.error('Ad not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = () => {
    if (!user) {
      toast.error('Please login to like');
      return;
    }
    setLiked(!liked);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!ad) return null;

  const hasLink = ad.link && ad.link.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Guest Banner */}
      {!user && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="max-w-3xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm">Login to interact with content</span>
              <Button
                onClick={() => navigate('/login')}
                size="sm"
                className="bg-white text-purple-600 hover:bg-purple-50 text-xs px-3 py-1 h-7"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="ad-detail-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Ad Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`ad-detail-${adId}`}>
          {/* Sponsored Badge Header */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-3 border-b border-purple-100">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium text-purple-600">Sponsored Content</span>
            </div>
          </div>

          {/* Full Image */}
          {ad.image_url && (
            <div className="w-full">
              <img
                src={getImageUrl(ad.image_url)}
                alt={ad.title}
                className="w-full max-h-[500px] object-contain bg-gray-50"
              />
            </div>
          )}
          
          {/* Content */}
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{ad.title}</h1>
            
            {ad.description && (
              <p className="text-gray-700 text-lg leading-relaxed mb-4 whitespace-pre-wrap">
                {ad.description}
              </p>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(ad.created_at)}</span>
              </div>
            </div>

            {/* CTA Button - if link exists */}
            {hasLink && (
              <a
                href={ad.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg mb-6"
                data-testid="ad-detail-cta-btn"
              >
                <ExternalLink className="h-5 w-5" />
                Visit Website
              </a>
            )}
          </div>

          {/* Action Bar */}
          <div className="flex items-center gap-4 px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                liked
                  ? 'text-red-500 bg-red-50'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="ad-detail-like-btn"
            >
              <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
              <span>{liked ? 'Liked' : 'Like'}</span>
            </button>
            
            <ShareButton
              title={ad.title}
              description={ad.description || ad.title}
              url={window.location.href}
              image={ad.image_url || ad.media_url}
              shareType="ad"
              itemId={ad.ad_id}
              variant="button"
              className="text-gray-600 hover:bg-gray-100"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdDetail;
